import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Printer, ArrowLeft, Download } from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';
import { GetMBLDetails } from '@/redux/Booking/actions';

const MBLPrint = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { bookingId, FILENUMBER } = location.state || {};
    const { MasterCompanyDetails } = useSelector((state) => state.common);

    const { id: idQuote } = useParams();
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const documentRef = useRef();

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                if (bookingId && bookingId !== 0) {
                    await _getBooking();
                }
            } catch (error) {
                console.error("Error initializing booking:", error);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [bookingId]);

    const _getBooking = async () => {
        try {
            let response;
            if (typeof bookingId === 'number' || (typeof bookingId === 'string' && bookingId !== '0')) {
                response = await dispatch(GetMBLDetails(bookingId));
                console.log('MBLdata:', response);
                if (response) {
                    setFormData(response);
                }
            } else if (bookingId && typeof bookingId === 'object') {
                setFormData(bookingId);
            }
        } catch (error) {
            console.error("Error fetching booking:", error);
        }
    };

    const handlePrint = () => {
        if (!documentRef.current) return;

        const printContent = documentRef.current.innerHTML;

        const inlinedCss = Array.from(document.styleSheets)
            .map((sheet) => {
                try {
                    return Array.from(sheet.cssRules || [])
                        .map((rule) => rule.cssText)
                        .join("\n");
                } catch (err) {
                    return sheet.href
                        ? `@import url("${sheet.href}");`
                        : "";
                }
            })
            .join("\n");

        const linkFallback = Array.from(
            document.querySelectorAll('link[rel="stylesheet"]')
        )
            .map((node) => node.outerHTML)
            .join("\n");

        const rootStyles = window.getComputedStyle(document.documentElement);
        const customProps = [];
        for (let i = 0; i < rootStyles.length; i++) {
            const name = rootStyles[i];
            if (name.startsWith("--")) {
                customProps.push(`${name}: ${rootStyles.getPropertyValue(name).trim()};`);
            }
        }
        const rootVarsCss = `:root { ${customProps.join(" ")} }`;

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        iframe.setAttribute("aria-hidden", "true");
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(`<!DOCTYPE html>
<html>
<head>
    <title>MASTER BILL OF LADING - ${formData?.MBL_NUMBER || "Document"}</title>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style id="__mbl_root_vars">${rootVarsCss}</style>
    <style id="__mbl_app_css">${inlinedCss}</style>
    ${linkFallback}
    <style id="__mbl_print_overrides">
        @page { size: A4; margin: 0; }
        html, body {
            margin: 0;
            padding: 0;
            background: #fff;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }
        .mbl-print-doc {
            width: 210mm !important;
            min-height: 297mm !important;
            padding: 8mm !important;
            margin: 0 !important;
            box-shadow: none !important;
            background: #fff !important;
            box-sizing: border-box !important;
            display: flex !important;
            flex-direction: column !important;
        }
        .mbl-print-doc *,
        .mbl-print-doc *::before,
        .mbl-print-doc *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }
        .mbl-print-doc .mbl-print-table,
        .mbl-print-doc .mbl-print-table > thead,
        .mbl-print-doc .mbl-print-table > tbody,
        .mbl-print-doc .mbl-print-table > tfoot,
        .mbl-print-doc .mbl-print-table > thead > tr,
        .mbl-print-doc .mbl-print-table > tbody > tr,
        .mbl-print-doc .mbl-print-table > tfoot > tr,
        .mbl-print-doc .mbl-print-table > thead > tr > td,
        .mbl-print-doc .mbl-print-table > tbody > tr > td,
        .mbl-print-doc .mbl-print-table > tfoot > tr > td {
            border: 0 !important;
            padding: 0 !important;
            background: transparent !important;
        }
        .watermark { display: none !important; }
    </style>
</head>
<body>
    <div class="mbl-print-doc" style="font-family: Arial, sans-serif;">
        ${printContent}
    </div>
</body>
</html>`);
        iframeDoc.close();

        const triggerPrint = () => {
            try {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
            } catch (err) {
                console.error("Print failed:", err);
            }
            const cleanup = () => {
                if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            };
            if (iframe.contentWindow) {
                iframe.contentWindow.addEventListener("afterprint", cleanup);
            }
            setTimeout(cleanup, 60000);
        };

        const waitForLoad = () => {
            const win = iframe.contentWindow;
            const doc = win.document;
            const images = Array.from(doc.images || []);
            const imagePromises = images.map((img) => {
                if (img.complete) return Promise.resolve();
                return new Promise((res) => {
                    img.onload = res;
                    img.onerror = res;
                });
            });
            const fontPromise = doc.fonts && doc.fonts.ready
                ? doc.fonts.ready
                : Promise.resolve();
            Promise.all([fontPromise, ...imagePromises]).then(() => {
                setTimeout(triggerPrint, 100);
            });
        };

        if (iframe.contentWindow.document.readyState === "complete") {
            waitForLoad();
        } else {
            iframe.onload = waitForLoad;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return moment(dateString).isValid() ? moment(dateString).format('DD MMM YYYY') : '';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-foreground">Loading master bill of lading data...</p>
                </div>
            </div>
        );
    }

    if (!formData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="text-red-600 mb-4">Failed to load master bill of lading data</p>
                    <button
                        onClick={() => navigate('/mbl', { state: { bookingId: bookingId, FILENUMBER: FILENUMBER } })}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to MBL
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-200 print:bg-white text-gray-900">
            {/* Action Bar */}
            <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex flex-col sm:flex-row items-center gap-2 sm:gap-0 sm:justify-between">
                    <button
                        onClick={() =>
                            navigate('/mbl', {
                                state: {
                                    bookingId: bookingId,
                                    FILENUMBER: FILENUMBER
                                },
                            })
                        }
                        className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all font-medium"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to MBL
                    </button>
                    <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                        <button
                            onClick={handlePrint}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all font-medium border border-gray-300 text-sm sm:text-base"
                        >
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">Download / Save as PDF</span>
                            <span className="sm:hidden">Download</span>
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-5 py-2 bg-[#1a2744] hover:bg-[#0f172a] text-white rounded-lg transition-all font-medium text-sm sm:text-base"
                        >
                            <Printer className="w-4 h-4" />
                            Print
                        </button>
                    </div>
                </div>
            </div>

            <div className="mbl-print-stage mx-auto px-2 sm:px-4 py-4 sm:py-6">
                <div
                    ref={documentRef}
                    className="mbl-print-doc bg-white shadow-lg print:shadow-none mx-auto"
                    style={{
                        fontFamily: "Arial, sans-serif",
                        width: "210mm",
                        minHeight: "297mm",
                        padding: "8mm",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <style>{`
                        @page {
                            size: A4;
                            margin: 0;
                        }

                        .mbl-print-stage {
                            overflow-x: auto;
                        }

                        @media print {
                            html, body {
                                margin: 0 !important;
                                padding: 0 !important;
                                background: #fff !important;
                                height: auto !important;
                                overflow: visible !important;
                            }

                            .mbl-print-doc {
                                width: 210mm !important;
                                min-height: 297mm !important;
                                padding: 8mm !important;
                                margin: 0 !important;
                                box-shadow: none !important;
                                background: #fff !important;
                                box-sizing: border-box !important;
                                page-break-inside: auto;
                                display: flex !important;
                                flex-direction: column !important;
                            }

                            .mbl-print-doc,
                            .mbl-print-doc *,
                            .mbl-print-doc *::before,
                            .mbl-print-doc *::after {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                                color-adjust: exact !important;
                            }

                            .watermark { display: none !important; }
                        }

                        .mbl-print-doc .bol-value {
                            color: #000 !important;
                            font-weight: 700 !important;
                            font-size: 10px !important;
                            letter-spacing: 0.02em;
                            text-transform: uppercase;
                            line-height: 1.3;
                        }

                        .mbl-print-doc .text-xs {
                            color: #1f2937;
                            font-size: 11px;
                            line-height: 1.3;
                        }

                        .mbl-print-doc .mbl-cargo-table {
                            width: 100%;
                            border-collapse: collapse;
                            table-layout: fixed;
                            margin: 0;
                        }

                        .mbl-print-doc .mbl-cargo-table th,
                        .mbl-print-doc .mbl-cargo-table td {
                            border: 1px solid #000;
                            padding: 4px 6px;
                            vertical-align: top;
                            line-height: 1.25;
                            text-align: left;
                        }

                        .mbl-print-doc .mbl-cargo-table thead th {
                            background-color: #d9d9d9;
                            color: #000;
                            font-weight: 800;
                            text-transform: uppercase;
                            letter-spacing: 0.03em;
                            font-size: 9px;
                            text-align: center;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }

                        .mbl-print-doc .mbl-cargo-table tbody td {
                            color: #1f2937;
                            font-size: 10px;
                        }

                        .mbl-print-doc .mbl-cargo-table tbody td.text-center { text-align: center; }

                        .mbl-print-header-block {
                            padding-bottom: 6px;
                        }

                        .mbl-print-doc .min-h-\\[70px\\]  { min-height: 50px !important; }
                        .mbl-print-doc .min-h-\\[60px\\]  { min-height: 50px !important; }
                        .mbl-print-doc .min-h-\\[40px\\]  { min-height: 26px !important; }

                        @media print {
                            .mbl-print-doc .bol-value {
                                color: #000 !important;
                                font-weight: 700 !important;
                                text-transform: uppercase !important;
                            }

                            .mbl-print-doc .mbl-cargo-table thead th,
                            .mbl-print-doc .mbl-cargo-table thead th * {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                                color-adjust: exact !important;
                            }

                            .mbl-print-doc .mbl-cargo-table,
                            .mbl-print-doc .mbl-bottom-section {
                                page-break-inside: avoid;
                                break-inside: avoid;
                            }

                            .mbl-print-doc .border,
                            .mbl-print-doc .border-t,
                            .mbl-print-doc .border-b,
                            .mbl-print-doc .border-l,
                            .mbl-print-doc .border-r {
                                border-style: solid !important;
                            }
                            .mbl-print-doc .border-black,
                            .mbl-print-doc .border-t.border-black,
                            .mbl-print-doc .border-b.border-black,
                            .mbl-print-doc .border-l.border-black,
                            .mbl-print-doc .border-r.border-black {
                                border-color: #000 !important;
                            }
                        }
                    `}</style>

                    {/* ── HEADER ── */}
                    <div className="mbl-print-header-block flex flex-col sm:flex-row justify-between items-start mb-2 gap-2">
                        <div className="flex items-center">
                            <div>
                                <img
                                    src={MasterCompanyDetails?.[0]?.CompanyLogoDark || ""}
                                    alt={MasterCompanyDetails?.[0]?.CompanyName || "Company Logo"}
                                    className="h-10 sm:h-12 w-auto"
                                />
                            </div>
                        </div>
                        <h1 className="text-2xl sm:text-4xl font-bold text-primary tracking-wide">
                            MASTER BILL OF LADING
                        </h1>
                    </div>

                    {/* ── CONTENT (flex:1 pushes footer to bottom) ── */}
                    <div style={{ flex: 1 }}>
                        {/* Main Grid */}
                        <div className="border border-black mbl-main-grid">
                            {/* Row 1 — Shipper / Booking / Reference */}
                            <div className="grid grid-cols-12">
                                <div className="col-span-6 border-r border-black">
                                    <div className="p-1 min-h-[70px]">
                                        <div className='bol-value'>SHIPPER (Principal or Seller-licensee and address including ZIP Code)</div>
                                        <div className='text-xs'>{formData?.SHIPPER || ''}<br /></div>
                                        <div className='text-xs'>
                                            {formData?.SHIPPER_ADDRESS
                                                ? formData.SHIPPER_ADDRESS.split('\r\n').map((line, index) => (
                                                    <span key={index}>{line}<br /></span>
                                                ))
                                                : MasterCompanyDetails?.[0]?.ProfileAddressPrint
                                                    ?.split(',')
                                                    .map((line, index) => (
                                                        <p key={index}>{line.trim()}</p>
                                                    ))
                                            }
                                        </div>
                                    </div>
                                </div>

                                <div className="col-span-4 border-r border-black">
                                    <div className="p-1 border-b border-black">
                                        <div className='bol-value'>BOOKING NUMBER</div>
                                        <div className="text-xs">
                                            {formData?.CARRIER_BOOKING_NO || ''}
                                        </div>
                                    </div>
                                    <div className="p-1">
                                        <div className='bol-value'>EXPORT REFERENCES</div>
                                        <div className='text-xs'>{formData?.EXPORT_REF_NUMBER || ' '}</div>
                                    </div>
                                </div>

                                <div className="col-span-2">
                                    <div className="p-1">
                                        <div className='bol-value'>REFERENCE NUMBER</div>
                                        <div className='text-xs text-center'>{formData?.MBL_NUMBER || ' '}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Row 2 — Consignee / Forwarding Agent / FMC */}
                            <div className="grid grid-cols-12 border-t border-black">
                                <div className="col-span-6 p-1 border-r border-black">
                                    <div className="bol-value">AGENT/CONSIGNEE</div>
                                    <div className="text-xs min-h-[60px]">
                                        {formData?.CONSIGNEE || ' '}
                                    </div>
                                </div>
                                <div className="col-span-4 p-1 border-r border-black">
                                    <div className="bol-value">FORWARDING AGENT (Name and address - references)</div>
                                    <div className="text-xs min-h-[60px]">
                                        {formData?.FORWARDING_AGENT || ''}<br />
                                        {formData?.FORWARDING_AGENT_ADDRESS ? (
                                            formData?.FORWARDING_AGENT_ADDRESS.split('\r\n').map((line, index) => (
                                                <span key={index}>{line}<br /></span>
                                            ))
                                        ) : (
                                            MasterCompanyDetails?.[0]?.ProfileAddressPrint
                                                ?.split(',')
                                                .map((line, index) => (
                                                    <p key={index}>{line.trim()}</p>
                                                ))
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-2 p-1">
                                    <div className="bol-value">FMC #</div>
                                    <div className="text-xs">{formData?.FMC_NO || ' '}</div>
                                </div>
                            </div>

                            {/* Row 3 — Notify / Also Notify */}
                            <div className="grid grid-cols-12 border-t border-black">
                                <div className="col-span-6 p-1 border-r border-black">
                                    <div className="bol-value">NOTIFY PARTY/INTERMEDIATE CONSIGNEE (Name and address)</div>
                                    <div className="text-xs min-h-[40px]">
                                        {formData?.NOTIFY || ''}<br />
                                        {formData?.NOTIFY_ADDRESS ? (
                                            formData?.NOTIFY_ADDRESS.split('\r\n').map((line, index) => (
                                                <span key={index}>{line}<br /></span>
                                            ))
                                        ) : (
                                            <span>&nbsp;</span>
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-6 p-1">
                                    <div className="bol-value">ALSO NOTIFY PARTY (Routing &amp; Instructions)</div>
                                    <div className="text-xs min-h-[40px]">
                                        {formData?.ALSO_NOTIFY || ''}<br />
                                        {formData?.ALSO_NOTIFY_ADDRESS ? (
                                            formData?.ALSO_NOTIFY_ADDRESS.split('\r\n').map((line, index) => (
                                                <span key={index}>{line}<br /></span>
                                            ))
                                        ) : (
                                            <span>&nbsp;</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Row 4 — Pre-Carriage / Place of Receipt */}
                            <div className="grid grid-cols-12 border-t border-black">
                                <div className="col-span-3 p-1 border-r border-black">
                                    <div className="bol-value">PRE-CARRIAGE BY</div>
                                    <div className="text-xs">{formData?.PRE_CARRIAGE_BY || ' '}</div>
                                </div>
                                <div className="col-span-9 p-1">
                                    <div className="bol-value">PLACE OF RECEIPT BY PRE-CARRIER</div>
                                    <div className="text-xs flex justify-between items-center">
                                        <span>{formData?.PLACE_OF_RECEIPT_CUSTOMIZED || formData?.PLACE_OF_RECEIPT || ' '}</span>
                                        <span className="text-red-600 font-bold">EXPRESS RELEASE</span>
                                    </div>
                                </div>
                            </div>

                            {/* Row 5 — Vessel / Port of Loading / Loading Pier / Foreign Port */}
                            <div className="grid grid-cols-12 border-t border-black">
                                <div className="col-span-3 p-1 border-r border-black">
                                    <div className="bol-value">VESSEL &amp; VOYAGE NO.</div>
                                    <div className="text-xs">
                                        {formData?.VESSEL_NAME || formData?.VOYAGE ? (
                                            `${formData?.VESSEL_NAME || ''}${formData?.VESSEL_NAME && formData?.VOYAGE ? ' / ' : ''}${formData?.VOYAGE || ''}`
                                        ) : '\u00A0'}
                                    </div>
                                </div>
                                <div className="col-span-3 p-1 border-r border-black">
                                    <div className="bol-value">PORT OF LOADING / EXPORT</div>
                                    <div className="text-xs">
                                        {formData?.PORT_OF_LOADING_CUSTOMIZED || formData?.PORT_OF_LOADING || ' '}
                                    </div>
                                </div>
                                <div className="col-span-3 p-1 border-r border-black">
                                    <div className="bol-value">LOADING PIER / TERMINAL</div>
                                    <div className="text-xs">{formData?.LOADING_PIER_TERMINAL || ' '}</div>
                                </div>
                                <div className="col-span-3 p-1">
                                    <div className="bol-value">FOREIGN PORT OF UNLOADING (Vessel and air only)</div>
                                    <div className="text-xs">
                                        {formData?.PORT_OF_DISCHARGE_CUSTOMIZED || formData?.PORT_OF_DISCHARGE || ' '}
                                    </div>
                                </div>
                            </div>

                            {/* Row 6 — Place of Delivery / Move Type / Containerized */}
                            <div className="grid grid-cols-12 border-t border-black">
                                <div className="col-span-6 p-1 border-r border-black">
                                    <div className="bol-value">PLACE OF DELIVERY BY ON-CARRIER</div>
                                    <div className="text-xs">
                                        {formData?.PLACE_OF_DELIVERY_CUSTOMIZED || formData?.PLACE_OF_DELIVERY || ' '}
                                    </div>
                                </div>
                                <div className="col-span-3 p-1 border-r border-black">
                                    <div className="bol-value">TYPE OF MOVE</div>
                                    <div className="text-xs">{formData?.MOVE_TYPE || ' '}</div>
                                </div>
                                <div className="col-span-3 p-1">
                                    <div className="bol-value">CONTAINERIZED (Vessel Only)</div>
                                    <div className="text-xs text-center">
                                        {formData?.TOTAL_CONTAINER > 0 ? 'YES' : 'NO'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CARGO SECTION */}
                        <table className="mbl-cargo-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '20%' }}>MARKS AND NUMBERS</th>
                                    <th style={{ width: '10%' }}>NUMBER OF PACKAGES</th>
                                    <th style={{ width: '40%' }}>DESCRIPTION OF COMMODITIES in schedule B detail</th>
                                    <th style={{ width: '15%' }}>GROSS WEIGHT (Kilos)</th>
                                    <th style={{ width: '15%' }}>MEASUREMENT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData?.MasterBillLoadingDescriptions?.map((cargo, index) => (
                                    <tr key={cargo.ID || index}>
                                        <td>
                                            {cargo.MARKS_NO || ''}<br />
                                            {cargo.CONTAINER_NUMBER} / {cargo.SEAL_NUMBER}
                                        </td>
                                        <td className="text-center">{cargo.NO_PACKAGE || ''}</td>
                                        <td>
                                            {cargo.DESCRIPTIONS || ''}
                                            {cargo.HAZ_CODE && (
                                                <div className="mt-1 text-red-600 font-semibold">HAZARDOUS: {cargo.HAZ_CODE}</div>
                                            )}
                                        </td>
                                        <td className="text-center">{cargo.KGS ? `${cargo.KGS} KGS` : ''}</td>
                                        <td className="text-center">{cargo.CBM ? `${cargo.CBM} CBM` : ''}</td>
                                    </tr>
                                ))}
                                {(!formData?.MasterBillLoadingDescriptions || formData.MasterBillLoadingDescriptions.length === 0) && (
                                    <>
                                        {[...Array(6)].map((_, i) => (
                                            <tr key={`blank-${i}`}>
                                                <td style={{ height: '28px' }}>&nbsp;</td>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                            </tr>
                                        ))}
                                    </>
                                )}
                            </tbody>
                        </table>

                        {/* Bottom notices */}
                        <div className="border border-black border-t-0 mbl-bottom-section">
                            <div className="p-1 border-b border-black">
                                <div className="text-[8px] uppercase tracking-wide">
                                    THESE COMMODITIES, SOFTWARE, OR TECHNOLOGY WERE EXPORTED FROM THE UNITED STATES IN ACCORDANCE WITH EXPORT ADMINISTRATION REGULATIONS. DIVERSION CONTRARY TO U.S. LAW PROHIBITED.
                                </div>
                            </div>
                            <div className="p-1 border-b border-black">
                                <div className="text-[9px] font-semibold leading-snug">
                                    Carrier has a policy against payment, solicitation, or receipt of any rebate, directly or indirectly, which would be unlawful under the United States Shipping Act, 1984 as amended. DECLARED VALUE _________________________ READ CLAUSE 29 HEREOF CONCERNING EXTRA FREIGHT AND CARRIER'S LIMITATIONS OF LIABILITY.
                                </div>
                            </div>
                            <div className="p-1">
                                <div className="bol-value">TO : Shipez.ai</div>
                                <div className="text-xs mt-1">
                                    <div className="bol-value">REMARK</div>
                                    <div>FOR B/L INSTRUCTIONS PLEASE CONTACT</div>
                                    {MasterCompanyDetails?.[0]?.ProfileAddressPrint
                                        ?.split(',')
                                        .map((line, index) => (
                                            <p key={index}>{line.trim()}</p>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* ── END CONTENT ── */}

                    {/* ── FOOTER — always at bottom of A4 page ── */}
                    <div className="text-[8px] text-gray-500 text-left mt-3">
                        Powered by {MasterCompanyDetails?.[0]?.CompanyName || 'Shipez.ai'}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MBLPrint;